import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BlogSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isInView, hasAnimated]);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("de-CH", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-gray-200 w-1/4 rounded"></div>
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-96 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="blog"
      className="section-padding bg-white"
      data-testid="blog-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16"
        >
          <div>
            <span className="label-text">Insights & Artikel</span>
            <h2 className="font-playfair text-3xl lg:text-4xl font-semibold text-lab-primary mt-4 max-w-2xl">
              Aktuelles aus der digitalen Welt
            </h2>
          </div>
          <button
            className="mt-6 lg:mt-0 text-lab-accent font-medium flex items-center gap-2 hover:gap-3 transition-all duration-200"
            data-testid="blog-view-all"
          >
            Alle Artikel
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              className="group"
              data-testid={`blog-post-${post.id}`}
            >
              <Link to={`/blog/${post.seo?.slug || post.id}`} className="block">
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden mb-6 bg-lab-bg-light">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80";
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 backdrop-blur-sm text-lab-primary px-3 py-1 text-xs font-semibold tracking-wide">
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-sm text-lab-text-muted">
                    <span>{formatDate(post.date)}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {post.read_time}
                    </span>
                  </div>

                  <h3 className="font-playfair text-xl font-semibold text-lab-primary group-hover:text-lab-accent transition-colors duration-200">
                    {post.title}
                  </h3>

                  <p className="text-lab-text-muted text-sm leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>

                  <div className="pt-2">
                    <span className="text-lab-accent font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                      Weiterlesen
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
